/**
 * The stable reason codes of the four-eyes payout surface (D385, ADR-0055). A console branches on these, never on a
 * detail string.
 */
export const PAYOUT_REASON_CODES = [
    'SELF_APPROVAL',
    /** D385 review W5F-01: nobody issues or approves a payout to their own (driver) account. */
    'SELF_PAYOUT',
    'UNATTRIBUTED_PAYOUT',
    'FINANCIAL_LIMIT_EXCEEDED',
    'IDENTITY_FROM_SESSION_ONLY',
    'PAYOUT_NOT_PENDING',
    'PAYOUT_ALREADY_DECIDED',
    'PAYOUT_CURRENCY_MISMATCH',
    'PAYOUT_CURRENCY_REQUIRED',
    'DECISION_REASON_TOO_LONG',
    'DRIVER_NOT_FOUND',
    // ---- W6-EARNINGS-LEDGER A2 (D392): the ledger is the cap, and the settlement rail has its own refusals ----
    /** The amount is above what the ledger owes this payee (the cash debt and the open requests are netted). */
    'PAYOUT_EXCEEDS_LEDGER_BALANCE',
    /** The server has no payout ledger: nothing can be approved, marked paid or marked failed (fail closed). */
    'PAYOUT_LEDGER_UNAVAILABLE',
    /** Only an approved (PROCESSING) payout can be marked paid or failed. */
    'PAYOUT_NOT_PROCESSING',
    /** Marking a payout paid needs the rail's own transfer reference. */
    'EXTERNAL_REF_REQUIRED',
    'FAILURE_REASON_REQUIRED',
    /** W6Y-04: the payout was approved before the ledger and reserved nothing — mark it failed and issue it again. */
    'PAYOUT_NOT_RESERVED',
];
export class FinanceApi {
    client;
    constructor(client) {
        this.client = client;
    }
    reconcile(marketId) {
        // Server contract: GET /v1/payments/reconcile?market=<marketId> (operator `ledger:view`), never a POST body. The
        // market is REQUIRED (MR-A Codex round-1 P1-3): reconciliation is per market and the server rejects an unscoped call.
        return this.client.request({ method: 'GET', path: `/v1/payments/reconcile?market=${encodeURIComponent(marketId)}`, auth: true });
    }
    /** Issue a driver payout (operator `payout:create`, the maker). D385: the maker is the session — never sent. */
    createPayout(input) {
        return this.client.request({ method: 'POST', path: '/v1/payouts', body: input, auth: true });
    }
    /** D385: the driver's market and the currency it uses, before the amount is typed (operator `payout:create`). */
    payoutContext(driverId) {
        return this.client.request({ method: 'GET', path: `/v1/payouts/context?driverId=${encodeURIComponent(driverId)}`, auth: true });
    }
    /** The operator payout queue for a market (default status REQUESTED, oldest first). Operator `payout:view`. */
    payoutQueue(query = {}) {
        const params = new URLSearchParams();
        if (query.market !== undefined && query.market !== '')
            params.set('market', query.market);
        if (query.status !== undefined)
            params.set('status', query.status);
        if (query.limit !== undefined)
            params.set('limit', String(query.limit));
        const qs = params.toString();
        return this.client.request({ method: 'GET', path: `/v1/payouts/queue${qs !== '' ? `?${qs}` : ''}`, auth: true });
    }
    /**
     * Approve or reject a REQUESTED payout with a reason (operator `payout:approve`, the checker; approval is
     * financial-limit gated). D385: the checker is the session; the maker's approval is refused `SELF_APPROVAL`.
     */
    decidePayout(payoutId, input) {
        return this.client.request({ method: 'POST', path: `/v1/payouts/${encodeURIComponent(payoutId)}/decision`, body: input, auth: true });
    }
    /**
     * A2 (D392): the transfer happened — record the rail's reference (`payout:settle`). The reservation moves out of
     * `platform:payout_pending` in the same transaction; the money has left the platform for good.
     */
    markPayoutPaid(payoutId, input) {
        return this.client.request({ method: 'POST', path: `/v1/payouts/${encodeURIComponent(payoutId)}/mark-paid`, body: input, auth: true });
    }
    /** A2 (D392): the transfer failed — the reservation returns to the payee, who can be paid again (`payout:settle`). */
    markPayoutFailed(payoutId, input) {
        return this.client.request({ method: 'POST', path: `/v1/payouts/${encodeURIComponent(payoutId)}/mark-failed`, body: input, auth: true });
    }
    /** D470: open the destination of an APPROVED (PROCESSING) payout to pay it (`payout:settle`; every open is audited). */
    revealPayoutDestination(payoutId, market) {
        return this.client.request({ method: 'GET', path: `/v1/payouts/${encodeURIComponent(payoutId)}/destination?market=${encodeURIComponent(market)}`, auth: true });
    }
}
function marketQs(query) {
    const params = new URLSearchParams();
    if (query.market !== undefined && query.market !== '')
        params.set('market', query.market);
    if (query.limit !== undefined)
        params.set('limit', String(query.limit));
    const qs = params.toString();
    return qs !== '' ? `?${qs}` : '';
}
export class CaseAdminApi {
    client;
    constructor(client) {
        this.client = client;
    }
    /** The operator safety queue — incidents still needing a human, oldest first. Operator `safety:view`. */
    listIncidents(query = {}) {
        return this.client.request({ method: 'GET', path: `/v1/safety/incidents${marketQs(query)}`, auth: true });
    }
    /**
     * D416: the OPS queue of a market — the open incidents drivers raised there, oldest first. Operator `ops:view` in the
     * market; the market is required (`400 MARKET_REQUIRED` without it).
     */
    listOpsIncidents(query) {
        return this.client.request({ method: 'GET', path: `/v1/ops/incidents${marketQs(query)}`, auth: true });
    }
    /** The operator risk-review queue (OPEN/UNDER_REVIEW/APPEALED), oldest first. Operator `safety:view`. */
    listRiskCases(query = {}) {
        return this.client.request({ method: 'GET', path: `/v1/risk/cases${marketQs(query)}`, auth: true });
    }
    /**
     * The operator ticket queue. D364: `market` (`cc:slug`) is REQUIRED in practice — the server authorizes
     * `support:view` at that market and, with no market, runs un-scoped, which under the fail-closed market RLS
     * returns zero rows. The caller passes a market from the operator's OWN grants, never the platform list.
     */
    ticketQueue(query = {}) {
        const params = new URLSearchParams({
            ...(query.market !== undefined && query.market !== '' ? { market: query.market } : {}),
            ...(query.status !== undefined ? { status: query.status } : {}),
            ...(query.limit !== undefined ? { limit: String(query.limit) } : {}),
        });
        const qs = params.toString();
        return this.client.request({ method: 'GET', path: `/v1/support/tickets${qs ? `?${qs}` : ''}`, auth: true });
    }
    assignTicket(ticketId) {
        return this.client.request({ method: 'POST', path: `/v1/support/tickets/${encodeURIComponent(ticketId)}/assign`, body: {}, auth: true });
    }
    ticketAction(ticketId, action) {
        return this.client.request({ method: 'POST', path: `/v1/support/tickets/${encodeURIComponent(ticketId)}/actions`, body: action, auth: true });
    }
    transitionIncident(incidentId, input) {
        return this.client.request({ method: 'POST', path: `/v1/safety/incidents/${encodeURIComponent(incidentId)}/transition`, body: input, auth: true });
    }
    transitionRiskCase(caseId, input) {
        return this.client.request({ method: 'POST', path: `/v1/risk/cases/${encodeURIComponent(caseId)}/transition`, body: input, auth: true });
    }
    /** The append-only case audit — the drill (append-only, RBAC-scoped, server-redacted). */
    caseAudit(type, caseId) {
        return this.client.request({ method: 'GET', path: `/v1/cases/${encodeURIComponent(type)}/${encodeURIComponent(caseId)}/audit`, auth: true });
    }
}
//# sourceMappingURL=admin-finance.js.map